import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { MessageType, NotificationService, NotificationQuery, NotificationType } from 'src/app/shared/notification';
import { ToastOptions } from './toast.types';

/** Reserved notification scope that isolates global toasts from scoped banners. */
export const TOAST_SCOPE = 'global-toast';

/** Default auto-dismiss delay applied when a caller does not override it. */
export const DEFAULT_DURATION_MS = 5000;

/**
 * App-wide, one-call facade for transient feedback. It is a thin layer over the
 * existing {@link NotificationService} + Akita store: there is no parallel
 * state. Toasts live in the shared store under {@link TOAST_SCOPE}; the host
 * subscribes to {@link toasts$} and renders them.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  /** Live, scope-filtered stream of toasts consumed by the host component. */
  readonly toasts$: Observable<NotificationType[]> = this.query.selectAll({
    filterBy: entity => entity.scope === TOAST_SCOPE
  });

  constructor(private notifications: NotificationService, private query: NotificationQuery) {}

  success(message: string, opts?: ToastOptions): string {
    return this.show(MessageType.success, message, opts);
  }

  error(message: string, opts?: ToastOptions): string {
    return this.show(MessageType.danger, message, opts);
  }

  info(message: string, opts?: ToastOptions): string {
    return this.show(MessageType.info, message, opts);
  }

  warning(message: string, opts?: ToastOptions): string {
    return this.show(MessageType.warning, message, opts);
  }

  dismiss(id: string): void {
    this.notifications.removeById(id);
  }

  dismissAll(): void {
    this.notifications.removeByScope(TOAST_SCOPE);
  }

  private show(type: MessageType, message: string, opts?: ToastOptions): string {
    return this.notifications.addMessage(TOAST_SCOPE, {
      type,
      isClose: opts?.closable ?? true,
      message,
      title: opts?.title,
      details: opts?.details,
      action: opts?.action,
      duration: opts?.duration ?? DEFAULT_DURATION_MS
    });
  }
}
